import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "utils/api";
import { useTeamLocale } from "./TeamLocaleContext";
import { formatEuro } from "./formatters";

const formatWhen = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPercent = (value) =>
  `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value || 0)} %`;

const centsToEuroInput = (cents) =>
  (Number(cents || 0) / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseEuroInputToCents = (raw, { allowZero = false } = {}) => {
  const text = String(raw || "").trim().replace(/€/g, "").replace(/\s/g, "");
  if (!text || text.endsWith(",") || text.endsWith(".")) return null;
  let normalized = text;
  if (/^\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?$/.test(text)) {
    normalized = text.replace(/\./g, "").replace(",", ".");
  } else if (text.includes(",") && text.includes(".")) {
    return null;
  } else {
    normalized = text.replace(",", ".");
  }
  const euros = Number(normalized);
  if (!Number.isFinite(euros)) return null;
  if (euros === 0) return allowZero ? 0 : null;
  if (euros < 0) return null;
  const cents = Math.round(euros * 100);
  if (cents < 1 || cents > 100_000_000) return null;
  return cents;
};

const parseRateInput = (raw) => {
  const text = String(raw || "").trim().replace("%", "").trim().replace(",", ".");
  if (!text || text.endsWith(".")) return null;
  const rate = Number(text);
  if (!Number.isFinite(rate) || rate < 0 || rate > 100) return null;
  return Math.round(rate * 100) / 100;
};

const shareCentsFor = (grossCents, rate) => {
  if (grossCents == null || rate == null) return null;
  return Math.round((grossCents * rate) / 100);
};

const rateForShare = (grossCents, shareCents) => {
  if (!grossCents || shareCents == null || shareCents > grossCents) return null;
  return Math.round((shareCents / grossCents) * 10000) / 100;
};

const draftFromEntry = (entry) => ({
  gross: centsToEuroInput(entry.grossCents),
  rate: String(entry.commissionPercent).replace(".", ","),
  share: centsToEuroInput(entry.employeeCents),
});

const EntryRow = ({ entry, t, showEmployee, onDraftChange, onDelete, resetToken, saving }) => {
  const [draft, setDraft] = useState(() => draftFromEntry(entry));

  useEffect(() => {
    setDraft(draftFromEntry(entry));
    // Only replace the draft when the saved amounts change, or the user discards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.grossCents, entry.commissionPercent, entry.employeeCents, resetToken]);

  const grossCents = parseEuroInputToCents(draft.gross, { allowZero: true });
  const rate = parseRateInput(draft.rate);
  const shareCents = parseEuroInputToCents(draft.share, { allowZero: true });
  const expectedShare = grossCents != null && rate != null ? shareCentsFor(grossCents, rate) : null;
  const changed = !(
    grossCents === entry.grossCents &&
    rate === entry.commissionPercent &&
    shareCents === entry.employeeCents
  );
  const valid = grossCents != null && rate != null && shareCents === expectedShare;

  useEffect(() => {
    onDraftChange(entry.id, changed ? { ...draft, valid } : null);
  }, [changed, valid, draft, entry.id, onDraftChange]);

  return (
    <>
      <tr className={saving ? "is-saving" : ""}>
        {showEmployee && <td>{entry.senderName || "—"}</td>}
        <td>{entry.companyName}</td>
        <td>{formatWhen(entry.occurredAt)}</td>
        <td>
          <input
            className="team-umsatz-entry-input"
            inputMode="decimal"
            aria-label={t("umsatz.colGross")}
            value={draft.gross}
            disabled={saving}
            onChange={(event) => {
              const gross = event.target.value;
              const share = shareCentsFor(parseEuroInputToCents(gross, { allowZero: true }), parseRateInput(draft.rate));
              setDraft((current) => ({
                ...current,
                gross,
                share: share == null ? current.share : centsToEuroInput(share),
              }));
            }}
          />
        </td>
        <td>
          <input
            className="team-umsatz-entry-input"
            inputMode="decimal"
            aria-label={t("umsatz.colRate")}
            value={draft.rate}
            disabled={saving}
            onChange={(event) => {
              const rate = event.target.value;
              const share = shareCentsFor(parseEuroInputToCents(draft.gross, { allowZero: true }), parseRateInput(rate));
              setDraft((current) => ({
                ...current,
                rate,
                share: share == null ? current.share : centsToEuroInput(share),
              }));
            }}
          />
        </td>
        <td>
          <input
            className="team-umsatz-entry-input"
            inputMode="decimal"
            aria-label={t("umsatz.colShare")}
            value={draft.share}
            disabled={saving}
            onChange={(event) => {
              const share = event.target.value;
              const rate = rateForShare(
                parseEuroInputToCents(draft.gross),
                parseEuroInputToCents(share, { allowZero: true })
              );
              setDraft((current) => ({
                ...current,
                share,
                rate: rate == null ? current.rate : String(rate).replace(".", ","),
              }));
            }}
          />
        </td>
        <td>
          <button
            type="button"
            className="team-umsatz-entry-delete"
            aria-label={`${t("umsatz.deleteEntry")}: ${entry.companyName}`}
            disabled={saving}
            onClick={() => onDelete(entry)}
          >
            {t("umsatz.deleteEntry")}
          </button>
        </td>
      </tr>
    </>
  );
};

const EntryTable = ({ entries, t, showEmployee, onDraftChange, onDelete, resetToken, saving }) => {
  if (!entries.length) {
    return <p className="team-muted">{t("umsatz.noEntriesForEmployee")}</p>;
  }
  return (
    <div className="team-table-wrap">
      <table className="team-table">
        <thead>
          <tr>
            {showEmployee && <th>{t("umsatz.colEmployee")}</th>}
            <th>{t("umsatz.colCompany")}</th>
            <th>{t("umsatz.colDate")}</th>
            <th>{t("umsatz.colGross")}</th>
            <th>{t("umsatz.colRate")}</th>
            <th>{t("umsatz.colShare")}</th>
            <th>{t("umsatz.colActions")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              t={t}
              showEmployee={showEmployee}
              onDraftChange={onDraftChange}
              onDelete={onDelete}
              resetToken={resetToken}
              saving={saving}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const totalsForEntries = (entries) =>
  entries.reduce(
    (totals, entry) => ({
      grossCents: totals.grossCents + entry.grossCents,
      employeeCents: totals.employeeCents + entry.employeeCents,
      count: totals.count + 1,
    }),
    { grossCents: 0, employeeCents: 0, count: 0 }
  );

const withoutEntry = (data, id) => {
  const employees = data.employees.map((employee) => {
    const entries = employee.entries.filter((entry) => entry.id !== id);
    return { ...employee, entries, totals: totalsForEntries(entries) };
  });
  const unassignedEntries = data.unassigned.entries.filter((entry) => entry.id !== id);
  return {
    ...data,
    employees,
    unassigned: { ...data.unassigned, entries: unassignedEntries, totals: totalsForEntries(unassignedEntries) },
    totals: totalsForEntries([
      ...employees.flatMap((employee) => employee.entries),
      ...unassignedEntries,
    ]),
  };
};

const DeleteEntryDialog = ({ entry, deleting, error, onCancel, onConfirm, t, locale }) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !deleting) onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [deleting, onCancel]);

  return createPortal(
    <div className="team-delete-dialog-root">
      <div
        className="team-delete-dialog-overlay"
        onClick={deleting ? undefined : onCancel}
        aria-hidden="true"
      />
      <div
        className="team-delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="umsatz-delete-title"
        aria-describedby="umsatz-delete-description"
      >
        <h2 id="umsatz-delete-title">{t("umsatz.deleteTitle")}</h2>
        <p id="umsatz-delete-description" className="team-delete-dialog-lead">
          {t("umsatz.deleteConfirm", {
            company: entry.companyName,
            amount: formatEuro(entry.grossCents, locale),
          })}
        </p>
        {error && <p className="team-umsatz-entry-error" role="alert">{error}</p>}
        <div className="team-delete-dialog-actions">
          <button type="button" className="team-dialog-cancel" autoFocus disabled={deleting} onClick={onCancel}>
            {t("common.cancel")}
          </button>
          <button type="button" className="team-dialog-confirm team-umsatz-delete-confirm" disabled={deleting} onClick={onConfirm}>
            {deleting ? t("umsatz.deleting") : t("umsatz.deletePermanently")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const emptyMitarbeiterForm = () => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  commissionPercent: "25",
});

const formFromEmployee = (employee) => ({
  firstName: employee?.firstName || "",
  lastName: employee?.lastName || "",
  email: employee?.email || "",
  phone: employee?.phone || "",
  password: "",
  commissionPercent: String(employee?.commissionPercent ?? 50).replace(".", ","),
});

const CreateMitarbeiterForm = ({ employee, onCreated, onCancel }) => {
  const { t } = useTeamLocale();
  const editing = Boolean(employee?.userId);
  const [form, setForm] = useState(() => (editing ? formFromEmployee(employee) : emptyMitarbeiterForm()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const setField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };
  const fieldId = (name) => `mitarbeiter-${editing ? employee.userId : "new"}-${name}`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      commissionPercent: form.commissionPercent,
    };
    if (form.password) payload.password = form.password;
    try {
      const { data } = editing
        ? await api.patch(`/admin/mitarbeiter-umsatz/employees/${employee.userId}`, payload)
        : await api.post("/admin/mitarbeiter-umsatz/employees", {
            ...payload,
            password: form.password,
          });
      if (editing) setForm((current) => ({ ...current, password: "" }));
      else setForm(emptyMitarbeiterForm());
      setSuccess(t(editing ? "umsatz.editSuccess" : "umsatz.createSuccess"));
      onCreated?.(data);
    } catch (err) {
      setError(err?.response?.data?.message || t(editing ? "umsatz.editError" : "umsatz.createError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="team-manual-form team-umsatz-create" onSubmit={handleSubmit}>
      <p className="team-lead">{t(editing ? "umsatz.editLead" : "umsatz.createLead")}</p>
      {error && <div className="auth-alert is-error">{error}</div>}
      {success && <div className="auth-alert is-success">{success}</div>}
      <div className="booking-step">
        <div className="address-grid">
          <div>
            <label htmlFor={fieldId("first-name")}>{t("umsatz.createFirstName")}</label>
            <input
              id={fieldId("first-name")}
              value={form.firstName}
              onChange={setField("firstName")}
              autoComplete="off"
              required
            />
          </div>
          <div>
            <label htmlFor={fieldId("last-name")}>{t("umsatz.createLastName")}</label>
            <input
              id={fieldId("last-name")}
              value={form.lastName}
              onChange={setField("lastName")}
              autoComplete="off"
              required
            />
          </div>
          <div>
            <label htmlFor={fieldId("email")}>{t("umsatz.createEmail")}</label>
            <input
              id={fieldId("email")}
              type="email"
              value={form.email}
              onChange={setField("email")}
              autoComplete="off"
              required
            />
          </div>
          <div>
            <label htmlFor={fieldId("phone")}>{t("umsatz.createPhone")}</label>
            <input
              id={fieldId("phone")}
              type="tel"
              value={form.phone}
              onChange={setField("phone")}
              autoComplete="off"
              placeholder="+49 …"
            />
            <p className="team-muted">{t("umsatz.createPhoneHint")}</p>
          </div>
          <div>
            <label htmlFor={fieldId("password")}>{t("umsatz.createPassword")}</label>
            <input
              id={fieldId("password")}
              type="password"
              value={form.password}
              onChange={setField("password")}
              autoComplete="new-password"
              minLength={8}
              required={!editing}
            />
            <p className="team-muted">
              {t(editing ? "umsatz.editPasswordHint" : "umsatz.createPasswordHint")}
            </p>
          </div>
          <div>
            <label htmlFor={fieldId("commission")}>{t("umsatz.commission")}</label>
            <input
              id={fieldId("commission")}
              inputMode="decimal"
              value={form.commissionPercent}
              onChange={setField("commissionPercent")}
            />
            <p className="team-muted">{t("umsatz.commissionHint")}</p>
          </div>
        </div>
        <div className="team-manual-form-actions">
          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting
              ? t(editing ? "umsatz.editSubmitting" : "umsatz.createSubmitting")
              : t(editing ? "umsatz.editSubmit" : "umsatz.createSubmit")}
          </button>
          {editing && (
            <button type="button" className="team-umsatz-cancel" onClick={onCancel} disabled={submitting}>
              {t("umsatz.editCancel")}
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

const MitarbeiterEditDialog = ({ employee, onClose, onCreated }) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div className="team-delete-dialog-root">
      <div className="team-delete-dialog-overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="team-delete-dialog team-umsatz-edit-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mitarbeiter-edit-title"
      >
        <h2 id="mitarbeiter-edit-title">{employee.name}</h2>
        <CreateMitarbeiterForm
          key={employee.userId}
          employee={employee}
          onCancel={onClose}
          onCreated={onCreated}
        />
      </div>
    </div>,
    document.body
  );
};

const MitarbeiterDirectory = ({ employees, onChanged }) => {
  const { t } = useTeamLocale();
  const [editingId, setEditingId] = useState(null);
  const editing = employees.find((employee) => employee.userId === editingId) || null;
  const closeEdit = useCallback(() => setEditingId(null), []);

  return (
    <div>
      <section className="team-umsatz-directory">
        <h2>{t("umsatz.directoryTitle")}</h2>
        {employees.length === 0 ? (
          <p className="team-muted">{t("umsatz.directoryEmpty")}</p>
        ) : (
          <div className="team-table-wrap">
            <table className="team-table">
              <thead>
                <tr>
                  <th>{t("umsatz.colEmployee")}</th>
                  <th>{t("umsatz.createEmail")}</th>
                  <th>{t("umsatz.createPhone")}</th>
                  <th>{t("umsatz.commission")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.userId} className={editingId === employee.userId ? "is-active" : ""}>
                    <td>{employee.name}</td>
                    <td>{employee.email || "—"}</td>
                    <td>{employee.phone || "—"}</td>
                    <td>{formatPercent(employee.commissionPercent)}</td>
                    <td className="team-table-actions">
                      <button type="button" onClick={() => setEditingId(employee.userId)}>
                        {t("umsatz.edit")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <CreateMitarbeiterForm onCreated={onChanged} />
      {editing && (
        <MitarbeiterEditDialog
          employee={editing}
          onClose={closeEdit}
          onCreated={(saved) => {
            closeEdit();
            onChanged(saved);
          }}
        />
      )}
    </div>
  );
};

const MitarbeiterUmsatzTab = () => {
  const { t, locale } = useTeamLocale();
  const [section, setSection] = useState("umsatz");
  const [data, setData] = useState(null);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingEntries, setPendingEntries] = useState({});
  const [resetToken, setResetToken] = useState(0);
  const [barSaving, setBarSaving] = useState(false);
  const [barError, setBarError] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);

  const load = useCallback(async (acceptedUserIds = []) => {
    setLoading(true);
    setError(null);
    const accepted = new Set(acceptedUserIds.filter(Boolean));
    try {
      const { data: payload } = await api.get("/admin/mitarbeiter-umsatz");
      setData(payload);
      setRates((current) => {
        const nextRates = {};
        (payload.employees || []).forEach((employee) => {
          const serverRate = String(employee.commissionPercent ?? 50).replace(".", ",");
          if (accepted.has(employee.userId)) {
            nextRates[employee.userId] = serverRate;
            return;
          }
          const existing = current[employee.userId];
          const parsed = parseRateInput(existing);
          const dirty = existing != null && (parsed == null || parsed !== employee.commissionPercent);
          nextRates[employee.userId] = dirty ? existing : serverRate;
        });
        return nextRates;
      });
    } catch (err) {
      setError(err?.response?.data?.message || t("umsatz.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleEmployeeChanged = useCallback(
    (saved) => load(saved?.userId ? [saved.userId] : []),
    [load]
  );

  useEffect(() => {
    load();
  }, [load]);

  const reportEntryDraft = useCallback((id, draft) => {
    setPendingEntries((current) => {
      if (!draft) {
        if (!current[id]) return current;
        const next = { ...current };
        delete next[id];
        return next;
      }
      const previous = current[id];
      if (
        previous &&
        previous.gross === draft.gross &&
        previous.rate === draft.rate &&
        previous.share === draft.share &&
        previous.valid === draft.valid
      ) {
        return current;
      }
      return { ...current, [id]: draft };
    });
  }, []);

  const totals = data?.totals || { grossCents: 0, employeeCents: 0, count: 0 };
  const employees = data?.employees || [];
  const unassigned = data?.unassigned;

  const dirtyRates = employees.filter((employee) => {
    const raw = rates[employee.userId];
    if (raw == null) return false;
    const parsed = parseRateInput(raw);
    if (parsed == null) return true;
    return parsed !== employee.commissionPercent;
  });
  const pendingList = Object.values(pendingEntries);
  const hasUnsaved = pendingList.length > 0 || dirtyRates.length > 0;
  const canConfirm =
    pendingList.every((draft) => draft.valid) &&
    dirtyRates.every((employee) => parseRateInput(rates[employee.userId]) != null);

  const confirmChanges = async () => {
    setBarSaving(true);
    setBarError(null);
    try {
      await Promise.all([
        ...Object.entries(pendingEntries).map(([id, draft]) =>
          api.patch(`/admin/mitarbeiter-umsatz/entries/${id}`, {
            amountEuro: draft.gross,
            commissionPercent: draft.rate,
          })
        ),
        ...dirtyRates.map((employee) =>
          api.patch(`/admin/mitarbeiter-umsatz/employees/${employee.userId}`, {
            commissionPercent: rates[employee.userId],
          })
        ),
      ]);
      await load();
    } catch (err) {
      setBarError(err?.response?.data?.message || t("umsatz.unsavedError"));
    } finally {
      setBarSaving(false);
    }
  };

  const declineChanges = () => {
    setBarError(null);
    const nextRates = {};
    employees.forEach((employee) => {
      nextRates[employee.userId] = String(employee.commissionPercent ?? 50).replace(".", ",");
    });
    setRates(nextRates);
    setResetToken((token) => token + 1);
  };

  const closeDelete = useCallback(() => {
    setDeleteEntry(null);
    setDeleteError(null);
  }, []);

  const confirmDelete = async () => {
    if (!deleteEntry || deleting) return;
    const id = deleteEntry.id;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/admin/mitarbeiter-umsatz/entries/${id}`);
      setData((current) => (current ? withoutEntry(current, id) : current));
      setPendingEntries((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      setDeleteSuccess(t("umsatz.deleteSuccess"));
      closeDelete();
    } catch (err) {
      setDeleteError(err?.response?.data?.message || t("umsatz.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="team-umsatz">
      <div className="team-umsatz-subtabs" role="tablist" aria-label="Mitarbeiter Umsatz">
        <button
          type="button"
          role="tab"
          aria-selected={section === "umsatz"}
          className={section === "umsatz" ? "is-active" : ""}
          onClick={() => setSection("umsatz")}
        >
          {t("umsatz.sectionUmsatz")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={section === "mitarbeiter"}
          className={section === "mitarbeiter" ? "is-active" : ""}
          onClick={() => setSection("mitarbeiter")}
        >
          {t("umsatz.sectionMitarbeiter")}
        </button>
      </div>

      {section === "mitarbeiter" && (
        <MitarbeiterDirectory employees={employees} onChanged={handleEmployeeChanged} />
      )}

      {section === "umsatz" && loading && !data && (
        <div className="team-empty">{t("common.loading")}</div>
      )}

      {section === "umsatz" && (!loading || data) && (
        <>
      <p className="team-lead">{t("umsatz.lead")}</p>
      {error && <div className="auth-alert is-error">{error}</div>}
      {deleteSuccess && <div className="auth-alert" role="status">{deleteSuccess}</div>}

      <div className="team-umsatz-summary">
        <div className="team-umsatz-stat">
          <span>{t("umsatz.totalGross")}</span>
          <strong>{formatEuro(totals.grossCents, locale)}</strong>
        </div>
        <div className="team-umsatz-stat">
          <span>{t("umsatz.totalShare")}</span>
          <strong>{formatEuro(totals.employeeCents, locale)}</strong>
        </div>
        <div className="team-umsatz-stat">
          <span>{t("umsatz.entryCount")}</span>
          <strong>{totals.count}</strong>
        </div>
      </div>

      {employees.length === 0 && !unassigned?.entries?.length ? (
        <p className="team-muted">{t("umsatz.empty")}</p>
      ) : (
        employees.map((employee) => (
          <section key={employee.userId} className="team-umsatz-person">
            <div className="team-umsatz-person-head">
              <div>
                <h2>{employee.name}</h2>
                {employee.email && <p className="team-muted">{employee.email}</p>}
                <p className="team-umsatz-person-total">
                  {t("umsatz.totalShare")}: {formatEuro(employee.totals.employeeCents, locale)}
                  {" · "}
                  {t("umsatz.totalGross")}: {formatEuro(employee.totals.grossCents, locale)}
                </p>
              </div>
              <div className="team-commission-form">
                <label htmlFor={`commission-${employee.userId}`}>
                  {t("umsatz.commission")}
                  <input
                    id={`commission-${employee.userId}`}
                    inputMode="decimal"
                    value={rates[employee.userId] ?? ""}
                    disabled={barSaving}
                    onChange={(event) =>
                      setRates((current) => ({
                        ...current,
                        [employee.userId]: event.target.value,
                      }))
                    }
                  />
                  <span>%</span>
                </label>
                <p className="team-muted">{t("umsatz.commissionHint")}</p>
              </div>
            </div>
            <EntryTable
              entries={employee.entries}
              t={t}
              onDraftChange={reportEntryDraft}
              onDelete={(entry) => {
                setDeleteSuccess(null);
                setDeleteEntry(entry);
              }}
              resetToken={resetToken}
              saving={barSaving || deleting}
            />
          </section>
        ))
      )}

      {unassigned?.entries?.length > 0 && (
        <section className="team-umsatz-person">
          <div className="team-umsatz-person-head">
            <div>
              <h2>{t("umsatz.unassigned")}</h2>
              <p className="team-muted">{t("umsatz.unassignedLead")}</p>
            </div>
          </div>
          <EntryTable
            entries={unassigned.entries}
            t={t}
            showEmployee
            onDraftChange={reportEntryDraft}
            onDelete={(entry) => {
              setDeleteSuccess(null);
              setDeleteEntry(entry);
            }}
            resetToken={resetToken}
            saving={barSaving || deleting}
          />
        </section>
      )}
        </>
      )}
      {deleteEntry && (
        <DeleteEntryDialog
          entry={deleteEntry}
          deleting={deleting}
          error={deleteError}
          onCancel={closeDelete}
          onConfirm={confirmDelete}
          t={t}
          locale={locale}
        />
      )}
      {createPortal(
        <div
          className={`team-unsaved-bar ${hasUnsaved ? "is-visible" : ""}`}
          role="region"
          aria-label={t("umsatz.unsaved")}
          aria-hidden={!hasUnsaved}
        >
          <p>{t("umsatz.unsaved")}</p>
          {barError && <p className="team-umsatz-entry-error">{barError}</p>}
          <div className="team-unsaved-bar-actions">
            <button type="button" onClick={declineChanges} disabled={!hasUnsaved || barSaving || deleting}>
              {t("umsatz.unsavedDecline")}
            </button>
            <button type="button" onClick={confirmChanges} disabled={!hasUnsaved || barSaving || deleting || !canConfirm}>
              {barSaving ? t("umsatz.unsavedSaving") : t("umsatz.unsavedConfirm")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MitarbeiterUmsatzTab;
